import { Telemetry, UIContext } from '@snowflake/core-ui';
import { ABExperimentName, FeatureFlag, SignupFlowType } from '@signup/shared';
import 'core-js/features/object/from-entries';
import 'core-js/features/object/values';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import 'regenerator-runtime/runtime';
import { SignupErrorBoundary } from './utils/ErrorBoundary';
import { SignupErrorPage } from './pages/SignupErrorPage';
import { SignupPageRoot } from './pages/SignupPageRoot';
import './styles/trial.scss';
import {
  AzureEventName,
  GcpEventName,
  resolveFormId,
  SignupEventName,
  SignupLogger,
} from './utils/SignupLogger';
import {
  AsyncIntlProvider,
  getLocale,
  languageFromLocale,
  loadMessages,
} from './utils/AsyncIntlProvider';
import {
  getSignupFlowTypeWithoutListener,
  getExperimentGroup,
  isFeatureEnabled,
} from './utils/configurations';
import { OidcCallbackPage } from './pages/OidcCallbackPage/OidcCallbackPage';

const awaitPolyfills = (async () => {
  const language = languageFromLocale(getLocale());
  let pluralRules;
  let pluralRulesLocaleData;
  let relativeTimeFormat;
  let relativeTimeFormatLocaleData;
  if (!Intl?.PluralRules) {
    pluralRules = import('@formatjs/intl-pluralrules/polyfill');
    pluralRulesLocaleData = import(`@formatjs/intl-pluralrules/locale-data/${language}`);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(Intl as any)?.RelativeTimeFormat) {
    relativeTimeFormat = import('@formatjs/intl-relativetimeformat/polyfill');
    relativeTimeFormatLocaleData = import(
      `@formatjs/intl-relativetimeformat/locale-data/${language}`
    );
  }
  await Promise.all([
    pluralRules,
    pluralRulesLocaleData,
    relativeTimeFormat,
    relativeTimeFormatLocaleData,
  ]);
})();

const awaitMessages = loadMessages(getLocale(), { isRoot: true });
const formId = resolveFormId(window.location.pathname);
const modifiedSignupFlowExperimentGroup = getExperimentGroup(ABExperimentName.MODIFIED_SIGNUP_FLOW);
const signupLogger = new SignupLogger(formId, modifiedSignupFlowExperimentGroup, 30000);
Telemetry.registerLogger(signupLogger.processEvent.bind(signupLogger));

const Root = () => (
  <UIContext name="SignupPage">
    <AsyncIntlProvider signupLogger={signupLogger}>
      <SignupErrorBoundary signupLogger={signupLogger}>
        <BrowserRouter>
          <Switch>
            <Route path="/azure" render={() => <SignupPageRoot {...{ formId }} />} />
            <Route path="/gcp/subscribe" render={() => <SignupPageRoot {...{ formId }} />} />
            <Route path="/developers" render={() => <SignupPageRoot {...{ formId }} />} />
            <Route path="/aws/capacity" render={() => <SignupPageRoot {...{ formId }} />} />
            <Route path="/aws/add-subscription" render={() => <SignupPageRoot {...{ formId }} />} />
            <Route path="/aws" render={() => <SignupPageRoot {...{ formId }} />} />
            {isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE) && (
              <Route path="/cortex-code" render={() => <SignupPageRoot {...{ formId }} />} />
            )}
            {/* Legacy Summit 2026 path: renders the default signup flow */}
            <Route path="/summit2026" render={() => <SignupPageRoot {...{ formId }} />} />
            {isFeatureEnabled(FeatureFlag.FF_ENABLE_OIDC_SIGNUP) && (
              <Route path="/oidc/callback" render={() => <OidcCallbackPage {...{ formId }} />} />
            )}
            <Route exact path="/" render={() => <SignupPageRoot {...{ formId }} />} />
            {/* 404 Page Not Found */}
            <Route render={() => <SignupPageRoot {...{ formId, pageNotFound: true }} />} />
          </Switch>
        </BrowserRouter>
      </SignupErrorBoundary>
    </AsyncIntlProvider>
  </UIContext>
);

const signupEvent = () => {
  switch (getSignupFlowTypeWithoutListener()) {
    case SignupFlowType.Azure:
      return AzureEventName.AZURE_ROOT_ERROR.toString();
    case SignupFlowType.Gcp:
      return GcpEventName.GCP_ROOT_ERROR.toString();
    default:
      return SignupEventName.SIGNUP_ROOT_ERROR.toString();
  }
};

const renderError = (error: Error) => {
  signupLogger.logError({
    component: 'Root',
    event: signupEvent(),
    data: {
      error: error.toString(),
      componentStack: 'Error occurred in Root while loading the application',
    },
  });
  const ErrorPage = () => (
    <UIContext name="SignupPage">
      <SignupErrorPage />
    </UIContext>
  );

  return ReactDOM.render(<ErrorPage />, document.getElementById('reactRoot') as HTMLElement);
};

document.addEventListener('DOMContentLoaded', async function () {
  try {
    try {
      await Promise.all([awaitMessages, awaitPolyfills]);
    } catch (err) {
      throw new Error(
        `Error when setting up localization: ${
          err instanceof Error ? err.toString() : String(err)
        }`,
      );
    }
    return ReactDOM.render(<Root />, document.getElementById('reactRoot') as HTMLElement);
  } catch (err) {
    renderError(err instanceof Error ? err : new Error(String(err)));
  }
});
